import type { RosterAgent, RosterEvent } from "@ark-bot/protocol";

import { text } from "./i18n.js";
import { ROSTER } from "./text/roster.js";

/**
 * The sidebar's rows, as the prototype's roster shows them: each agent with
 * what was last said, when, and whether it needs the user. Everything here is
 * a pure function of the roster stream (ARCHITECTURE §5.5), so the list is
 * the same after a reload as it was before.
 */

/** Folds one roster event into the rows; the stream sends rows whole, so there is nothing to merge. */
export function applyRosterEvent(rows: readonly RosterAgent[], event: RosterEvent): RosterAgent[] {
  switch (event.type) {
    case "roster":
      return [...event.agents];
    case "agent":
      return rows.some((row) => row.id === event.agent.id) ? rows.map((row) => (row.id === event.agent.id ? event.agent : row)) : [...rows, event.agent];
    case "agent.deleted":
      return rows.filter((row) => row.id !== event.id);
  }
}

/** Most recently moved first, as the prototype orders a section; ties keep their names in order. */
export function orderRoster(rows: readonly RosterAgent[]): RosterAgent[] {
  return [...rows].sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
}

/** "now", "5m", "3h", "2d": the prototype's row time. */
export function relativeTime(updatedAt: number, now: number): string {
  const t = text(ROSTER);
  const seconds = Math.max(0, Math.round((now - updatedAt) / 1000));
  if (seconds < 60) return t.justNow;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t.minutes(minutes);
  const hours = Math.round(minutes / 60);
  return hours < 24 ? t.hours(hours) : t.days(Math.round(hours / 24));
}

/** Markdown read as one line of plain words, for a preview. */
export function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(#{1,6}\s+|>\s*|[-*+]\s+|\d+\.\s+)/gm, "")
    .replace(/(\*\*|__|\*|_|~~)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export interface Preview {
  /** One line: what was last said, or what the agent is waiting on. */
  readonly text: string;
  /** A card is waiting on the user, as the prototype's "Waiting for you" row. */
  readonly attention: boolean;
}

/** What the row says under the name, from the agent's newest chat entry; null when nothing has been said. */
export function previewOf(agent: RosterAgent): Preview | null {
  const entry = agent.lastEntry;
  if (entry == null) return null;
  const t = text(ROSTER);
  switch (entry.kind) {
    case "user": {
      const said = entry.text.trim().length > 0 ? stripMarkdown(entry.text) : entry.attachments.length > 0 ? t.photos(entry.attachments.length) : "";
      return { text: t.you(said), attention: false };
    }
    case "agent": {
      if (entry.text != null && entry.text.trim().length > 0) return { text: stripMarkdown(entry.text), attention: false };
      if (entry.images != null && entry.images.length > 0) return { text: t.photos(entry.images.length), attention: false };
      if (entry.file != null) return { text: t.sentFile(entry.file.name), attention: false };
      return { text: "", attention: false };
    }
    case "room":
      return { text: `${entry.author.name}: ${stripMarkdown(entry.text)}`, attention: false };
    case "approval":
      return entry.status === "pending" ? { text: t.waitingForYou(entry.summary), attention: true } : { text: entry.summary, attention: false };
    case "handoff":
      return entry.status === "waiting" ? { text: t.needsYouOnScreen(entry.instruction), attention: true } : { text: entry.instruction, attention: false };
    case "form":
      return entry.status === "waiting" ? { text: t.askingYou(entry.title), attention: true } : { text: entry.title, attention: false };
    case "login":
      return { text: t.askingYou(new URL(entry.challenge.origin).hostname), attention: entry.status === "waiting" };
    case "peer":
      return { text: `${entry.direction === "out" ? t.toPeer(entry.peer.name) : entry.peer.name}: ${stripMarkdown(entry.text)}`, attention: false };
    case "contact": {
      // A meeting card's text already says who and when: "Budi would like to meet Thu 17 Sep 16:00–16:30: …".
      if (entry.mode === "meeting") return { text: entry.status === "open" ? t.toBookOrDecline(entry.text) : entry.text, attention: entry.status === "open" };
      const said = entry.mode === "question" ? t.contactAsks(entry.contact.name, entry.text) : t.contactLeftMessage(entry.contact.name, entry.text);
      return { text: said, attention: entry.status === "open" };
    }
    case "notice":
      return { text: entry.text, attention: true };
  }
}

/** The search box: a row matches on its name, its label, or what was last said. */
export function matchesQuery(agent: RosterAgent, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  const haystack = [agent.name, agent.config.title ?? "", previewOf(agent)?.text ?? ""].join("\n").toLowerCase();
  return haystack.includes(needle);
}

/**
 * Something arrived that the user has not looked at: the newest entry is not
 * their own and is newer than the last time they had this chat open. The
 * host does not know which chat is on screen, so this is the client's to
 * keep (the prototype tracks it per agent too).
 */
export function isUnread(agent: RosterAgent, seenAt: number | undefined): boolean {
  const entry = agent.lastEntry;
  if (entry == null || entry.kind === "user") return false;
  return entry.createdAt > (seenAt ?? 0);
}
